import {
  CAREPLAN_CHANGE_ATTRIBUTES,
  CAREPLAN_PRODUCT_ROLE,
} from "@/domains/careplan/models/constants";
import { SUBSCRIPTION_STATUS } from "@/domains/subscriptions/models/constants";
import {
  CURRENT_CAREPLAN_PRODUCTS,
  DAILY_BALANCE_TINCTURE,
  MORNING_FOCUS_CAPSULES,
  QUICK_EASE_INHALER,
} from "@/mocks/data/careplan-products";
import { MOCK_CATALOG } from "@/mocks/data/catalog";
import { createSubscription } from "@/mocks/data/subscriptions";
import { isoDaysFromNow } from "@/mocks/dates";
import { PERSONA_ID, type PersonaId } from "@/mocks/personas";
import type {
  CareplanChangeWire,
  CareplanMetaWire,
  CareplanProductWire,
  CareplansWire,
  PreviousCareplanProductWire,
} from "@/mocks/types";

/* The product that replaced an earlier one, as the backend reports it on an updated plan. */
const replacedProduct = (
  previous: PreviousCareplanProductWire,
  product: CareplanProductWire,
): CareplanChangeWire => {
  return {
    attribute: CAREPLAN_CHANGE_ATTRIBUTES.PRODUCT,
    entity: product.name,
    from: previous.name,
    to: product.name,
    role: product.role,
  };
};

const createApprovedCareplan = (): CareplansWire => {
  return {
    current: {
      id: 5101,
      version: 1,
      products: CURRENT_CAREPLAN_PRODUCTS,
      readAt: null,
      updatedAt: isoDaysFromNow(-1.2),
      wasBuiltFromSurvey: false,
      subscriptions: [],
    },
    previous: null,
    progressTeaser: null,
    progressEnabled: false,
  };
};

const createSubscribedCareplan = (): CareplansWire => {
  return {
    current: {
      id: 5301,
      version: 3,
      products: CURRENT_CAREPLAN_PRODUCTS,
      readAt: isoDaysFromNow(-21),
      updatedAt: isoDaysFromNow(-21.8),
      wasBuiltFromSurvey: true,
      subscriptions: [
        createSubscription({
          status: SUBSCRIPTION_STATUS.ACTIVE,
          acceptedAt: isoDaysFromNow(-60),
          lines: [
            { variantId: MOCK_CATALOG.DAILY_BALANCE.variantId, quantity: 2 },
            { variantId: MOCK_CATALOG.QUICK_EASE.variantId, quantity: 1 },
            { variantId: MOCK_CATALOG.NIGHT_REST.variantId, quantity: 1 },
          ],
        }),
      ],
    },
    previous: null,
    progressTeaser: {
      body: "You have logged 4 check-ins so far. See how your goals are trending.",
    },
    progressEnabled: true,
  };
};

const createNotSubscribedCareplan = (): CareplansWire => {
  return {
    current: {
      id: 5401,
      version: 2,
      products: CURRENT_CAREPLAN_PRODUCTS,
      readAt: isoDaysFromNow(-45),
      updatedAt: isoDaysFromNow(-46),
      wasBuiltFromSurvey: false,
      subscriptions: [
        createSubscription({
          status: SUBSCRIPTION_STATUS.CANCELLED,
          acceptedAt: isoDaysFromNow(-90),
          paymentMethod: { cardBrand: "mastercard", expiryDate: "08/28", lastDigits: "4444" },
          lines: [
            { variantId: MOCK_CATALOG.DAILY_BALANCE.variantId, quantity: 1 },
            { variantId: MOCK_CATALOG.QUICK_EASE.variantId, quantity: 1 },
            { variantId: MOCK_CATALOG.NIGHT_REST.variantId, quantity: 1 },
          ],
        }),
      ],
    },
    previous: { id: 5400, products: [MORNING_FOCUS_CAPSULES] },
    progressTeaser: null,
    progressEnabled: false,
  };
};

export const createInitialCareplan = (personaId: PersonaId): CareplansWire | null => {
  switch (personaId) {
    case PERSONA_ID.APPROVED:
      return createApprovedCareplan();
    case PERSONA_ID.SUBSCRIBED:
      return createSubscribedCareplan();
    case PERSONA_ID.NOT_SUBSCRIBED:
      return createNotSubscribedCareplan();
    case PERSONA_ID.IN_REVIEW:
      return null;
  }
};

export const createInitialCareplanMeta = (personaId: PersonaId): CareplanMetaWire => {
  switch (personaId) {
    /* A first plan: nothing to be new or updated against. */
    case PERSONA_ID.APPROVED:
      return { changes: [] };
    case PERSONA_ID.SUBSCRIBED:
      return {
        changes: [
          {
            attribute: CAREPLAN_CHANGE_ATTRIBUTES.DOSAGE,
            entity: QUICK_EASE_INHALER.name,
            from: "1 puff",
            to: "1-2 puffs",
            role: CAREPLAN_PRODUCT_ROLE.ADHOC,
          },
        ],
      };
    /* Version 2 replaced Morning Focus with Daily Balance: that card reads New Recommendation. */
    case PERSONA_ID.NOT_SUBSCRIBED:
      return { changes: [replacedProduct(MORNING_FOCUS_CAPSULES, DAILY_BALANCE_TINCTURE)] };
    case PERSONA_ID.IN_REVIEW:
      return { changes: [] };
  }
};
